import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';

const { FiDownload, FiFilter, FiBarChart3, FiFileText, FiDollarSign, FiUsers, FiCreditCard, FiArrowUp, FiArrowDown, FiAlertTriangle } = FiIcons;

const Reports = () => {
  const { user } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    type: 'invoices',
    start_date: format(new Date(new Date().setDate(1)), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    doctor_id: '',
    status: '',
    include_flagged: false,
    transaction_type: 'all' // all, credit, debit
  });

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      const response = await axios.get('/api/doctors');
      setDoctors(response.data);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      // FIXED: Check if user is trying to include flagged transactions without admin access
      if (filters.include_flagged && user?.role !== 'admin') {
        alert('Only administrators can include flagged transactions in reports.');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams(filters);
      const response = await axios.get(`/api/reports/export?${params}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${filters.type}_report_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
      
      // FIXED: Better error handling for different types of errors
      if (error.response?.status === 403) {
        alert('Access denied. You do not have permission to export this report or include flagged transactions.');
      } else if (error.response?.data?.error) {
        alert(`Export failed: ${error.response.data.error}`);
      } else {
        alert('Failed to export report. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const reportTypes = [
    {
      id: 'invoices',
      name: 'Invoices Report',
      description: 'Export invoice data with patient and payment information',
      icon: FiFileText,
      color: 'bg-blue-500'
    },
    {
      id: 'payouts',
      name: 'Doctor Payouts Report',
      description: 'Export doctor payout calculations and history',
      icon: FiDollarSign,
      color: 'bg-green-500'
    },
    {
      id: 'patient-visits',
      name: 'Patient Visit Report',
      description: 'Comprehensive patient visit data with invoice and payment details',
      icon: FiUsers,
      color: 'bg-purple-500'
    },
    {
      id: 'transactions',
      name: 'Transactions Report',
      description: 'View and export all account transactions with filtering options',
      icon: FiCreditCard,
      color: 'bg-amber-500'
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-grey-900">Reports & Analytics</h1>
      </div>

      {/* Report Type Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {reportTypes.map((type) => (
          <motion.div
            key={type.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`bg-white shadow rounded-lg p-6 cursor-pointer border-2 transition-colors ${
              filters.type === type.id 
                ? 'border-maroon-500 bg-maroon-50' 
                : 'border-transparent hover:border-grey-300'
            }`}
            onClick={() => setFilters({ ...filters, type: type.id })}
          >
            <div className="flex items-center">
              <div className={`${type.color} rounded-lg p-3 mr-4`}>
                <SafeIcon icon={type.icon} className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-grey-900">{type.name}</h3>
                <p className="text-sm text-grey-600">{type.description}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center mb-4">
          <SafeIcon icon={FiFilter} className="w-5 h-5 text-grey-400 mr-2" />
          <h3 className="text-lg font-medium text-grey-900">Report Filters</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-grey-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
            />
          </div>

          {(filters.type === 'invoices' || filters.type === 'patient-visits' || filters.type === 'payouts') && (
            <div>
              <label className="block text-sm font-medium text-grey-700 mb-1">
                Doctor
              </label>
              <select
                className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                value={filters.doctor_id}
                onChange={(e) => setFilters({ ...filters, doctor_id: e.target.value })}
              >
                <option value="">All Doctors</option>
                {doctors.map((doctor) => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {(filters.type === 'invoices' || filters.type === 'patient-visits') && (
            <div>
              <label className="block text-sm font-medium text-grey-700 mb-1">
                Status
              </label>
              <select
                className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All Statuses</option>
                <option value="paid">Paid</option>
                <option value="partial">Partial</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </div>
          )}
          
          {/* Transaction Type Filter - Only show for Transactions report */}
          {filters.type === 'transactions' && (
            <div className="col-span-2">
              <label className="block text-sm font-medium text-grey-700 mb-1">
                Transaction Type
              </label>
              <div className="flex flex-wrap gap-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="h-4 w-4 text-maroon-600 focus:ring-maroon-500"
                    checked={filters.transaction_type === 'all'}
                    onChange={() => setFilters({ ...filters, transaction_type: 'all' })}
                  />
                  <span className="ml-2 text-grey-700">All Transactions</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="h-4 w-4 text-green-600 focus:ring-green-500"
                    checked={filters.transaction_type === 'credit'}
                    onChange={() => setFilters({ ...filters, transaction_type: 'credit' })}
                  />
                  <span className="ml-2 text-grey-700 flex items-center">
                    <FiArrowUp className="text-green-500 mr-1" /> Credit
                  </span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="h-4 w-4 text-red-600 focus:ring-red-500"
                    checked={filters.transaction_type === 'debit'}
                    onChange={() => setFilters({ ...filters, transaction_type: 'debit' })}
                  />
                  <span className="ml-2 text-grey-700 flex items-center">
                    <FiArrowDown className="text-red-500 mr-1" /> Debit
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* FIXED: Only show flagged transactions checkbox for admin users and applicable report types */}
        {user?.role === 'admin' && (
          <div className="mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                className="rounded border-grey-300 text-maroon-600 focus:ring-maroon-500"
                checked={filters.include_flagged}
                onChange={(e) => setFilters({ ...filters, include_flagged: e.target.checked })}
              />
              <span className="ml-2 text-sm text-grey-700">
                Include flagged transactions
              </span>
            </label>
            <p className="text-xs text-grey-500 mt-1">
              When checked, adds a "Flagged" column to the report and includes flagged transactions in the data.
            </p>
          </div>
        )}

        {/* REMOVED: Warning message for managers about flagged transactions */}
      </div>

      {/* Export Section */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-grey-900">Export Report</h3>
            <p className="text-sm text-grey-600">
              Download the selected report with applied filters as CSV
            </p>
            {filters.type === 'patient-visits' && (
              <div className="mt-2 text-sm text-blue-600">
                <p className="font-medium">Patient Visit Report includes:</p>
                <p>Invoice details, appointment info, patient demographics, payment breakdown, and manual deductions</p>
                {user?.role === 'admin' && filters.include_flagged && (
                  <p className="text-purple-600 mt-1">• Flagged column will be included</p>
                )}
              </div>
            )}
            {filters.type === 'transactions' && (
              <div className="mt-2 text-sm text-blue-600">
                <p className="font-medium">Transactions Report includes:</p>
                <p>Date, account type, transaction type (debit/credit), amount, reference, and notes for all account transactions</p>
                {user?.role === 'admin' && filters.include_flagged && (
                  <p className="text-purple-600 mt-1">• Flagged transactions will be included</p>
                )}
              </div>
            )}
            {filters.type === 'invoices' && user?.role === 'admin' && (
              <div className="mt-2 text-sm text-blue-600">
                {filters.include_flagged ? (
                  <p className="text-purple-600">• Flagged column will be included</p>
                ) : (
                  <p className="text-grey-600">• Flagged transactions excluded, no flagged column</p>
                )}
              </div>
            )}
            {filters.type === 'payouts' && user?.role === 'admin' && (
              <div className="mt-2 text-sm text-blue-600">
                {filters.include_flagged ? (
                  <p className="text-purple-600">• Including flagged column in report</p>
                ) : (
                  <p className="text-grey-600">• Flagged appointments excluded from report</p>
                )}
              </div>
            )}
          </div>
          <button
            onClick={handleExport}
            disabled={loading}
            className="bg-maroon-600 hover:bg-maroon-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <SafeIcon icon={FiDownload} className="w-5 h-5" />
            <span>{loading ? 'Generating...' : 'Export CSV'}</span>
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white"
        >
          <div className="flex items-center">
            <SafeIcon icon={FiBarChart3} className="w-8 h-8 mr-3" />
            <div>
              <p className="text-blue-100">This Month</p>
              <p className="text-2xl font-semibold">Revenue Analytics</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white"
        >
          <div className="flex items-center">
            <SafeIcon icon={FiFileText} className="w-8 h-8 mr-3" />
            <div>
              <p className="text-green-100">Total</p>
              <p className="text-2xl font-semibold">Invoice Reports</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white"
        >
          <div className="flex items-center">
            <SafeIcon icon={FiUsers} className="w-8 h-8 mr-3" />
            <div>
              <p className="text-purple-100">Patient</p>
              <p className="text-2xl font-semibold">Visit Reports</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Reports;